import { Component, signal } from '@angular/core';
import { SetupWizardContentForm } from '../../../../../forms/setup-wizard/setup-wizard-content-form/setup-wizard-content-form';
import { SetupWizardContentsTable } from './setup-wizard-contents-table/setup-wizard-contents-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SetupWizardPayloadObjectKeys, SetupWizardContent, SetupWizardContentFormPayload } from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { ContentService } from '../../../../../../services/content.service';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
	selector: 'app-setup-wizard-content-editor',
	imports: [
		SetupWizardContentForm,
		SetupWizardContentsTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		NzButtonModule,
		NzTooltipModule,
	],
	standalone: true,
	templateUrl: './setup-wizard-content-editor.html',
	styleUrl: './setup-wizard-content-editor.scss',
})
export class SetupWizardContentEditor {
	public edit = signal<SetupWizardContent | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	public rubricCode = signal<string | undefined>(undefined);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard,
		private contentService: ContentService
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardContentFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.contents, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
		this.rubricCode.set(undefined);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
		this.rubricCode.set(undefined);
	}

	public async onEdit($event: SetupWizardContent) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);

			const beforeAfter = $event.after ?? $event.before;

			if (beforeAfter && beforeAfter.content_uuid) {
				this.rubricCode.set(await this.contentService.getRubricCode$(beforeAfter.content_uuid));
			}
		}
	}

	public async onToggleDisable($event: SetupWizardContent) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No content data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.contents, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardContent) {
		if (!$event.after) {
			throw new Error('No content data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.contents, $event.after);
	}

	public async onRemove($event: SetupWizardContent) {
		if (!$event.after) {
			throw new Error('No content data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.contents, $event.after);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
